import { useQuery } from '@apollo/client';
import { generateClient } from 'aws-amplify/api';
import { listMessagesByChatRoom, onSendMessageSecure } from '../../graphql/messaging';
import { useEffect, useState, useCallback, useRef } from 'react';
import { mergeMessagesById } from './messageState';

const amplifyClient = generateClient();

/** Newest-first page size; older messages load via scroll + fetchMore(nextToken). */
const MESSAGES_PAGE_SIZE = 40;

export const useMessagesSubscription = (chatRoomId) => {
  const [messages, setMessages] = useState([]);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const hasOlderPagesLoadedRef = useRef(false);

  const {
    data: queryData,
    loading: queryLoading,
    error: queryError,
    refetch,
    fetchMore,
  } = useQuery(listMessagesByChatRoom, {
    variables: {
      chatRoomID: chatRoomId || '',
      sortDirection: 'DESC',
      limit: MESSAGES_PAGE_SIZE,
    },
    skip: !chatRoomId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    context: { skipGlobalError: true },
  });

  useEffect(() => {
    hasOlderPagesLoadedRef.current = false;
    // Immediately clear stale messages when switching rooms so
    // the UI never shows the previous conversation's thread.
    setMessages([]);
  }, [chatRoomId]);

  // A slower query response must not overwrite a message that arrived from
  // the mutation or subscription in the meantime.
  useEffect(() => {
    if (!chatRoomId) {
      setMessages([]);
      return;
    }
    if (!queryData?.messagesByChatRoomIDAndCreatedAt) {
      return;
    }
    const items = queryData.messagesByChatRoomIDAndCreatedAt.items || [];
    setMessages((current) => mergeMessagesById(current, [...items].reverse(), chatRoomId));
  }, [chatRoomId, queryData]);

  const upsertMessage = useCallback((message) => {
    if (!message || message.chatRoomID !== chatRoomId) return;
    setMessages((current) => mergeMessagesById(current, [message], chatRoomId));
  }, [chatRoomId]);

  const nextToken = queryData?.messagesByChatRoomIDAndCreatedAt?.nextToken ?? null;
  const hasMoreOlder = Boolean(nextToken);

  const loadOlderMessages = useCallback(async () => {
    if (!chatRoomId || !nextToken || loadingOlder) return;

    setLoadingOlder(true);
    try {
      await fetchMore({
        variables: { nextToken },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.messagesByChatRoomIDAndCreatedAt?.items) return prev;
          const prevItems = prev?.messagesByChatRoomIDAndCreatedAt?.items ?? [];
          const nextItems = fetchMoreResult.messagesByChatRoomIDAndCreatedAt.items;
          return {
            ...prev,
            messagesByChatRoomIDAndCreatedAt: {
              ...prev.messagesByChatRoomIDAndCreatedAt,
              items: [...prevItems, ...nextItems],
              nextToken: fetchMoreResult.messagesByChatRoomIDAndCreatedAt.nextToken,
            },
          };
        },
      });
      hasOlderPagesLoadedRef.current = true;
    } catch (e) {
      console.warn('[useMessagesSubscription] loadOlderMessages failed', e);
    } finally {
      setLoadingOlder(false);
    }
  }, [chatRoomId, nextToken, loadingOlder, fetchMore]);

  // Subscribe to new messages using AWS Amplify client (handles WebSocket automatically)
  const subscriptionRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  useEffect(() => {
    if (!chatRoomId) {
      return;
    }

    let subscriptionSetup = false;
    let disposed = false;
    const refetchMessages = () => {
      if (disposed) return;
      Promise.resolve(refetch()).catch(() => {});
    };
    const startPolling = () => {
      if (disposed || pollingIntervalRef.current) return;
      pollingIntervalRef.current = setInterval(refetchMessages, 5000);
    };

    try {
      const subscription = amplifyClient.graphql({
        query: onSendMessageSecure,
        variables: {
          chatRoomID: chatRoomId,
        },
      });

      if (subscription && 'subscribe' in subscription) {
        subscriptionSetup = true;

        subscriptionRef.current = subscription.subscribe({
          next: ({ data }) => {
            const newMessage = data?.onSendMessageSecure;
            if (!newMessage) return;

            if (newMessage.chatRoomID !== chatRoomId) return;
            upsertMessage(newMessage);
          },
          error: () => {
            console.warn('[useMessagesSubscription] WebSocket subscription failed, using polling fallback');
            startPolling();
          },
        });
      }
    } catch (err) {
      console.warn('[useMessagesSubscription] Could not setup subscription, using polling fallback');
      startPolling();
    }

    if (!subscriptionSetup) {
      startPolling();
    }

    return () => {
      disposed = true;
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current.unsubscribe();
        } catch (err) {
          // Ignore unsubscribe errors
        }
        subscriptionRef.current = null;
      }

      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [chatRoomId, refetch, upsertMessage]);

  const fetchMessages = useCallback(() => {
    if (refetch) {
      refetch();
    }
  }, [refetch]);

  // Refetch on window focus for system messages that might miss subscriptions.
  // If the user already paged older messages in, skip refetch so Apollo does not collapse to the first page only.
  useEffect(() => {
    if (!chatRoomId) return;

    const handleFocus = () => {
      if (hasOlderPagesLoadedRef.current) return;
      if (refetch) refetch();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) return;
      if (hasOlderPagesLoadedRef.current) return;
      if (refetch) refetch();
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [chatRoomId, refetch]);

  const hasCachedMessages = Boolean(queryData?.messagesByChatRoomIDAndCreatedAt?.items?.length);

  return {
    messages,
    loading: Boolean(chatRoomId && queryLoading && !hasCachedMessages),
    error: queryError,
    refetch: fetchMessages,
    upsertMessage,
    loadOlderMessages,
    hasMoreOlder,
    loadingOlder,
  };
};

export default useMessagesSubscription;
